import React, { useState, useEffect, useRef } from 'react';
import { findMockResponse, ChatMessage, DirectorRoleKey } from '../../demo/projects';
import { buildProjectAIContext } from '../../demo/projects/llm-context';
import {
  apiEnsureSession, apiAppendMessage, apiListSessions, apiSessionMessages,
  apiListPerformanceSnapshots, apiActivatePerformanceSnapshot,
  SessionSummary, PerformanceSnapshot, ApiError,
} from '../../services/server-api';
import { sendAIChatStream, CreativePayload } from '../../services/ai';
import { useProject } from '../../context/ProjectContext';
import { useAuth } from '../../context/AuthContext';
import Markdown from '../../components/Markdown';
import CreativeMessageCard from '../../components/CreativeMessageCard';
import DynamicUpdateNotice from '../../components/DynamicUpdateNotice';
import { formatWan } from '../../utils/format';
import { DIRECTOR_PROMPTS } from '../../config/directorPrompts';
import QuickMenuPopover from '../../components/QuickMenuPopover';
import './AIDirector.css';

// 快捷话术按智能体独立，数据源：config/directorPrompts.ts（见组件内 agentPrompts）

// 三个策略角色的展示信息（骨架统一，数据按项目隔离）
const ROLES: Array<{ key: DirectorRoleKey; label: string; sub: string; title: string; desc: string }> = [
  { key: 'optimization', label: '优化', sub: '投放策略', title: '优化 · 投放策略助手', desc: '回答投放表现、素材跑量、方向取舍问题' },
  { key: 'planning', label: '创意', sub: '内容策划', title: '创意 · 素材策划助手', desc: '回答素材方向、实拍口播、情景剧、AI剧情、脚本策划与审核' },
  { key: 'editing', label: '后期', sub: '二次创作', title: '后期 · 素材制作助手', desc: '回答素材分析、前贴、后端、混剪、重剪与前三秒优化' },
];

// 角色化等待阶段文案（首个作为发送后 100ms 级即时反馈；后续以服务端真实 stage 事件为准）
const STAGE_HINTS: Record<DirectorRoleKey, string[]> = {
  optimization: ['正在读取当前投放数据…', '正在分析账户表现…', '正在整理优化建议…'],
  planning: ['正在理解创作需求…', '正在加载产品与人群认知…', '正在生成脚本结构…'],
  editing: ['正在分析素材表现…', '正在筛选素材…', '正在生成混剪方案…', '正在整理后期建议…'],
};

const roleLabel = (role: string) => ROLES.find(r => r.key === role)?.label ?? role;

type UiMessage = ChatMessage & { creative?: CreativePayload | null };

const emptyMessages = (): Record<DirectorRoleKey, UiMessage[]> => ({ optimization: [], planning: [], editing: [] });

// 每个智能体一份独立任务状态：并发生成时互不阻塞、切换 tab 不打断、结果写回各自角色
type RoleTaskState = {
  loading: boolean;
  stageMessage: string | null;
  elapsed: number;
  streamText: string;
  error: string | null;
  failedQuestion: string | null;
  finishedAt: number | null; // 刚完成（用于角色标签短暂 ✓）
};
const idleTask = (): RoleTaskState => ({
  loading: false, stageMessage: null, elapsed: 0, streamText: '', error: null, failedQuestion: null, finishedAt: null,
});
const emptyTaskState = (): Record<DirectorRoleKey, RoleTaskState> => ({
  optimization: idleTask(), planning: idleTask(), editing: idleTask(),
});

const AIDirector: React.FC = () => {
  const { currentProject } = useProject();
  const { user, llmReady, updateQuota } = useAuth();
  const [activeRole, setActiveRole] = useState<DirectorRoleKey>('optimization');
  // 三个角色分别维护独立会话：优化中聊过的内容不会混到创意/后期里
  const [messagesMap, setMessagesMap] = useState<Record<DirectorRoleKey, UiMessage[]>>(emptyMessages);
  // 并发发送时 runChat 需要读到最新线程（闭包里的 state 可能落后一个事件循环）
  const messagesMapRef = useRef(messagesMap);
  messagesMapRef.current = messagesMap;
  const [inputMessage, setInputMessage] = useState('');
  // 每角色独立 loading / 阶段文案 / 已用时 / 流式缓冲 / 错误（全局 loading 会让三个智能体互相锁死）
  const [tasks, setTasks] = useState<Record<DirectorRoleKey, RoleTaskState>>(emptyTaskState);
  const [switchNotice, setSwitchNotice] = useState<string | null>(null);
  // 历史提问记录抽屉
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [expanded, setExpanded] = useState<{
    session: { id: number; projectName: string; role: string; title: string };
    messages: Array<{ role: 'user' | 'assistant'; content: string; time: string }>;
  } | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const prevProjectIdRef = useRef(currentProject.id);
  const projectIdRef = useRef(currentProject.id);
  // 服务端会话ID缓存：每个 账号×项目×角色 一个会话（服务端幂等创建）
  const sessionIdMapRef = useRef<Map<string, number>>(new Map());
  // 演示投放数据快照（当前产品的 DEMO 快照列表与激活项）
  const [perfSnapshots, setPerfSnapshots] = useState<PerformanceSnapshot[]>([]);
  const [perfActiveId, setPerfActiveId] = useState<number | null>(null);
  const [perfSwitching, setPerfSwitching] = useState(false);
  // 右侧工作台上下文折叠（默认展开）
  const [contextCollapsed, setContextCollapsed] = useState(false);
  // 每角色独立 AbortController：切换角色只影响当前角色，一个智能体的取消不会打断其他两个
  const abortRefs = useRef<Record<DirectorRoleKey, AbortController | null>>({
    optimization: null, planning: null, editing: null,
  });
  const startedAtRef = useRef<Record<DirectorRoleKey, number>>({ optimization: 0, planning: 0, editing: 0 });

  const patchTask = (role: DirectorRoleKey, patch: Partial<RoleTaskState>) =>
    setTasks(prev => ({ ...prev, [role]: { ...prev[role], ...patch } }));

  const task = tasks[activeRole];
  const loading = task.loading;

  const messages = messagesMap[activeRole];
  const activeRoleMeta = ROLES.find(r => r.key === activeRole)!;
  // 当前智能体专属快捷话术（首次推荐 + 浮层全量），随 activeRole 切换
  const agentPrompts = DIRECTOR_PROMPTS[activeRole];
  const [quickMenuOpen, setQuickMenuOpen] = useState(false);
  const activePerfSnapshot = perfSnapshots.find(s => s.id === perfActiveId) || null;
  const quotaExhausted = (user?.remainingQuota ?? 1) <= 0;
  const quotaLow = !quotaExhausted && (user?.remainingQuota ?? 99) <= 5;

  // 发送/回复完成/切换角色后：消息容器精确定位到底部（不波及页面级滚动）
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messagesMap, tasks, activeRole]);

  // 已用时本地计时：每个角色各自计时，只对「正在生成」的角色生效（不预测剩余时间）
  const anyLoading = tasks.optimization.loading || tasks.planning.loading || tasks.editing.loading;
  useEffect(() => {
    if (!anyLoading) return;
    const timer = setInterval(() => {
      setTasks(prev => {
        let changed = false;
        const next = { ...prev };
        (Object.keys(prev) as DirectorRoleKey[]).forEach(r => {
          if (!prev[r].loading) return;
          const elapsed = Math.floor((Date.now() - startedAtRef.current[r]) / 1000) * 1000;
          if (elapsed !== prev[r].elapsed) { next[r] = { ...prev[r], elapsed }; changed = true; }
        });
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [anyLoading]);

  // 切换项目后清空三个角色的本地会话（服务端历史保留），避免知识串项目
  useEffect(() => {
    if (prevProjectIdRef.current !== currentProject.id) {
      prevProjectIdRef.current = currentProject.id;
      projectIdRef.current = currentProject.id;
      setMessagesMap(emptyMessages());
      setTasks(prev => {
        (Object.keys(prev) as DirectorRoleKey[]).forEach(r => abortRefs.current[r]?.abort());
        return emptyTaskState();
      });
      setSwitchNotice(`已切换至「${currentProject.name}」，已开启新的策略会话。`);
    }
  }, [currentProject.id, currentProject.name]);

  // 演示投放数据：跟随项目加载快照列表（无快照的产品不显示该区块）
  useEffect(() => {
    let cancelled = false;
    setPerfSnapshots([]);
    setPerfActiveId(null);
    apiListPerformanceSnapshots(currentProject.id)
      .then(result => {
        if (cancelled) return;
        setPerfSnapshots(result.snapshots);
        setPerfActiveId(result.active_id);
      })
      .catch(() => { /* 快照加载失败不阻塞导演台 */ });
    return () => { cancelled = true; };
  }, [currentProject.id]);

  // 切换演示快照：后续数据问答均基于新快照
  const handleSnapshotSwitch = async (snapshotId: number) => {
    if (perfSwitching) return;
    setPerfSwitching(true);
    try {
      const result = await apiActivatePerformanceSnapshot(currentProject.id, snapshotId);
      setPerfSnapshots(result.snapshots);
      setPerfActiveId(result.active_id);
    } catch {
      // 切换失败保持原状态，不打断会话
    } finally {
      setPerfSwitching(false);
    }
  };

  const handleRoleChange = (role: DirectorRoleKey) => {
    setActiveRole(role);
    setInputMessage('');
    setQuickMenuOpen(false);
  };

  // 记录到服务端历史（尽力而为，失败不影响展示）
  const record = async (sessionId: number, role: 'user' | 'assistant', content: string) => {
    try {
      await apiAppendMessage(sessionId, role, content);
    } catch {
      // 历史追加失败不影响展示
    }
  };

  // 发送/重试共用：isRetry=true 时用户消息已在会话线程中，不再重复追加
  // 走流式端点 /api/ai/chat/stream：
  // - 立即 ack/stage → 角色化阶段文案 + 已用时计时
  // - 普通文本回答 delta 实时追加（真流式）
  // - 结构化任务（creative/editing 卡片）阶段进度 + final 一次性渲染卡片
  // 并发：三个角色各自独立请求 / 独立 AbortController / 独立状态；切换 tab 不取消，结果按 role 写回
  const runChat = async (question: string, isRetry: boolean) => {
    const role = activeRole;
    const base = messagesMapRef.current[role];
    const userMessage: ChatMessage = { role: 'user', content: question, timestamp: Date.now() };
    // 多轮上下文：携带该角色最近对话（服务端再截取最近20条）
    const thread = isRetry ? base : [...base, userMessage];
    if (!isRetry) {
      setMessagesMap(prev => ({ ...prev, [role]: thread }));
    }
    startedAtRef.current[role] = Date.now();
    const controller = new AbortController();
    abortRefs.current[role] = controller;
    patchTask(role, {
      loading: true,
      error: null,
      failedQuestion: null,
      streamText: '',
      stageMessage: STAGE_HINTS[role]?.[0] || '正在分析…',
      elapsed: 0,
      finishedAt: null,
    });

    const sentProjectId = currentProject.id;
    const sessionKey = `${currentProject.id}:${role}`;

    try {
      // 会话幂等创建：同 项目×角色 复用服务端会话
      let sessionId = sessionIdMapRef.current.get(sessionKey);
      if (!sessionId) {
        const ensured = await apiEnsureSession(currentProject.id, currentProject.name, role);
        sessionId = ensured.sessionId;
        sessionIdMapRef.current.set(sessionKey, sessionId);
      }

      if (!llmReady) {
        // 演示模式（未配置 Provider）：预置回复，不扣额度
        await new Promise(resolve => setTimeout(resolve, 600));
        const mockContent = findMockResponse(question, currentProject, role, thread);
        await record(sessionId, 'user', question);
        await record(sessionId, 'assistant', mockContent);
        if (projectIdRef.current !== sentProjectId) return;
        pushAssistant(role, thread, mockContent);
        return;
      }

      // 流式请求：服务端校验额度 → 路由（程序秒回/结构化PROGRESS/普通STREAM）→ 成功才扣减
      const result = await sendAIChatStream(
        {
          project_id: currentProject.id,
          role,
          messages: thread.map(m => ({ role: m.role, content: m.content })),
          project_context: buildProjectAIContext(currentProject),
        },
        {
          onStage: (ev) => patchTask(role, { stageMessage: ev.message, ...(typeof ev.elapsed === 'number' ? { elapsed: ev.elapsed } : {}) }),
          onDelta: (piece) => setTasks(prev => ({ ...prev, [role]: { ...prev[role], streamText: prev[role].streamText + piece } })),
        },
        controller.signal,
      );
      if (result.remaining_questions !== undefined) updateQuota(Math.max(0, (user?.questionLimit ?? 0) - result.remaining_questions));

      if (projectIdRef.current !== sentProjectId) return;
      pushAssistant(role, thread, result.content, result.creative || null);
      await record(sessionId, 'user', question);
      await record(sessionId, 'assistant', result.content);
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        // 用户主动停止：不写错误、不记历史（服务端按未完成处理，不扣次数）
        return;
      }
      if (projectIdRef.current !== sentProjectId) return;
      if (e instanceof ApiError && e.code === 'QUESTION_LIMIT_REACHED') {
        patchTask(role, { error: e.message });
        if (user) updateQuota(user.questionLimit);
      } else if (e instanceof ApiError && (e.code === 'STREAM_INTERRUPTED' || e.code === 'AI_PROVIDER_ERROR')) {
        // 流式中途断开 / Provider 全不可用：不扣次数，保留用户原问题并提供重试
        patchTask(role, { error: e.message, failedQuestion: question });
        await record(sessionIdRef(sessionKey), 'user', question).catch(() => {});
      } else {
        patchTask(role, { error: `请求失败（${e instanceof Error ? e.message : String(e)}），请重试`, failedQuestion: question });
      }
    } finally {
      if (abortRefs.current[role] === controller) abortRefs.current[role] = null;
      const finishedAt = Date.now();
      patchTask(role, { loading: false, streamText: '', stageMessage: null, elapsed: 0, finishedAt });
      // 角色标签上的短暂 ✓（3s 后自动消失，不做复杂动画）
      setTimeout(() => {
        setTasks(prev => (prev[role].finishedAt === finishedAt ? { ...prev, [role]: { ...prev[role], finishedAt: null } } : prev));
      }, 3000);
    }
  };

  // 停止当前角色的生成（只影响该角色，其他两个继续）
  const stopGeneration = () => {
    abortRefs.current[activeRole]?.abort();
  };

  // 辅助：取会话ID（重试路径下会话已确保存在）
  const sessionIdRef = (sessionKey: string): number => sessionIdMapRef.current.get(sessionKey) ?? 0;

  const pushAssistant = (role: DirectorRoleKey, thread: UiMessage[], content: string, creative?: CreativePayload | null) => {
    const aiMessage: UiMessage = { role: 'assistant', content, timestamp: Date.now(), creative: creative || null };
    setMessagesMap(prev => ({ ...prev, [role]: [...thread, aiMessage] }));
  };

  const sendMessage = () => {
    const question = inputMessage.trim();
    if (!question) return;
    // 同一角色串行：该角色还在生成时再次提交给出明确提示（其他角色不受影响）
    if (tasks[activeRole].loading) {
      patchTask(activeRole, { error: `当前${activeRoleMeta.label}任务正在生成，请等待完成或停止当前任务。` });
      return;
    }
    setInputMessage('');
    void runChat(question, false);
  };

  const retryLast = () => {
    if (!task.failedQuestion || loading) return;
    void runChat(task.failedQuestion, true);
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setExpanded(null);
    try {
      const result = await apiListSessions();
      setSessions(result.sessions);
    } catch {
      setSessions([]);
    }
  };

  const viewSession = async (id: number) => {
    try {
      const result = await apiSessionMessages(id);
      setExpanded(result);
    } catch {
      // 忽略加载失败
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    setInputMessage(prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="strategy-page">
     <div className="strategy-main-col">
      {switchNotice && (
        <div className="switch-notice">{switchNotice}</div>
      )}
      {task.error && (
        <div className="switch-notice switch-notice-warning">
          <span>{task.error}</span>
          {task.failedQuestion && (
            <button className="notice-retry" onClick={retryLast} disabled={loading}>
              重试
            </button>
          )}
        </div>
      )}

      {/* 顶部导航：三智能体 + AI在线 + 剩余提问 + 历史入口 */}
      <div className="director-topbar">
        <div className="role-segmented" role="tablist">
          {ROLES.map(role => (
            <button
              key={role.key}
              role="tab"
              aria-selected={activeRole === role.key}
              className={`role-segment ${activeRole === role.key ? 'active' : ''}`}
              onClick={() => handleRoleChange(role.key)}
            >
              <span className="role-segment-glyph">{role.label.slice(0, 1)}</span>
              <span className="role-segment-text">
                <span className="role-segment-name">{role.label}</span>
                <span className="role-segment-sub">{role.sub}</span>
              </span>
              {/* 角色级状态点：● 生成中 / ✓ 刚完成（3s），不做复杂动画 */}
              {tasks[role.key].loading
                ? <span className="role-status-dot busy" title="正在生成" />
                : tasks[role.key].finishedAt ? <span className="role-status-dot done" title="已完成">✓</span> : null}
            </button>
          ))}
        </div>
        <div className="director-topbar-right">
          <span className={`llm-badge ${llmReady ? 'llm-badge-on' : 'llm-badge-off'}`}>
            <span className={`llm-dot ${llmReady ? 'on' : 'off'}`} />
            {llmReady ? 'AI 在线' : '预置回复 · 未配置API'}
          </span>
          {user && (
            <span className={`quota-chip ${quotaExhausted ? 'quota-chip-zero' : quotaLow ? 'quota-chip-low' : ''}`}>
              剩余提问 {user.remainingQuota}/{user.questionLimit}
            </span>
          )}
          <button className="history-btn" onClick={openHistory}>
            历史提问记录
          </button>
        </div>
      </div>

      {/* 动态更新提示（单行、弱化样式） */}
      <DynamicUpdateNotice className="strategy-update-notice" />

      {/* 对话工作区 */}
      <div className="chat-workspace">
        <div className="messages-container" ref={messagesScrollRef}>
          {messages.length === 0 ? (
            <div className="strategy-empty">
              <h3>{agentPrompts.emptyTitle}</h3>
              <p className="strategy-empty-desc">
                {agentPrompts.emptyDesc} 基于当前项目「{currentProject.name}」的认知与数据作答。
              </p>
              <div className="task-cards">
                {agentPrompts.firstSix.map((prompt, i) => (
                  <button
                    key={prompt}
                    className="task-card-item"
                    onClick={() => handleQuickPrompt(prompt)}
                    disabled={quotaExhausted}
                  >
                    <span className="task-card-index">{String(i + 1).padStart(2, '0')}</span>
                    <span className="task-card-text">{prompt}</span>
                    <span className="task-card-arrow" aria-hidden="true">→</span>
                  </button>
                ))}
              </div>
              <p className="strategy-empty-hint">点击话术填入输入框，可修改后发送 · Enter 发送 · Shift+Enter 换行</p>
            </div>
          ) : (
            <div className="thread">
              {messages.map((message, index) => (
                message.role === 'user' ? (
                  <div className="user-turn" key={index}>
                    <div className="user-bubble">{message.content}</div>
                  </div>
                ) : (
                  <div className="ai-turn" key={index}>
                    <div className="ai-turn-head">
                      <span className="ai-avatar">{activeRoleMeta.label.slice(0, 1)}</span>
                      <span className="ai-turn-name">AI策略 · {roleLabel(activeRole)}</span>
                    </div>
                    <div className="ai-turn-body">
                      {message.creative ? (
                        <CreativeMessageCard creative={message.creative} content={message.content} />
                      ) : (
                        <Markdown content={message.content} />
                      )}
                    </div>
                  </div>
                )
              ))}
              {loading && (
                task.streamText ? (
                  // STREAM 模式：真实增量内容持续追加（final 前按纯文本展示，避免半截 Markdown 抖动）
                  <div className="ai-turn">
                    <div className="ai-turn-head">
                      <span className="ai-avatar">{activeRoleMeta.label.slice(0, 1)}</span>
                      <span className="ai-turn-name">AI策略 · {roleLabel(activeRole)}</span>
                    </div>
                    <div className="ai-turn-body streaming">
                      <p className="stream-text">{task.streamText}</p>
                      <span className="stream-caret" aria-hidden="true" />
                    </div>
                  </div>
                ) : (
                  // STRUCTURED_PROGRESS / 首反馈：真实阶段文案 + 已用时（无假百分比、无假定时）
                  <div className="ai-turn">
                    <div className="ai-turn-head">
                      <span className="ai-avatar">{activeRoleMeta.label.slice(0, 1)}</span>
                      <span className="ai-turn-name">AI策略 · {roleLabel(activeRole)}</span>
                    </div>
                    <div className="ai-turn-body thinking">
                      <span className="dot" /><span className="dot" /><span className="dot" />
                      <span className="thinking-text">{task.stageMessage || (llmReady ? '正在分析…' : '正在组织回答')}</span>
                      {task.elapsed >= 3000 && (
                        <span className="thinking-elapsed">已用时 {Math.floor(task.elapsed / 1000)} 秒</span>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* 工作台输入区 */}
        <div className="composer">
          <div className={`composer-box ${quotaExhausted ? 'composer-disabled' : ''}`}>
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={quotaExhausted ? '本期提问额度已用完，请联系管理员调整配额' : `向「${activeRoleMeta.label} · ${activeRoleMeta.sub}」提问，如：${currentProject.director[activeRole].quickPrompts[0]}`}
              onKeyDown={handleKeyDown}
              disabled={quotaExhausted}
              rows={2}
            />
            <div className="composer-foot">
              <div className="composer-foot-left">
                <button
                  type="button"
                  className="quick-menu-btn"
                  onClick={() => setQuickMenuOpen(o => !o)}
                  disabled={quotaExhausted}
                >
                  <span aria-hidden="true">☰</span> 快捷话术
                </button>
                <span className="composer-hint">Enter 发送 · Shift+Enter 换行 · 回答基于当前项目认知与合规规则</span>
              </div>
              {loading ? (
                <button className="btn send-btn stop-btn" onClick={stopGeneration}>
                  停止生成
                </button>
              ) : (
                <button
                  className="btn btn-primary send-btn"
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || quotaExhausted}
                >
                  发送
                </button>
              )}
            </div>
            {quickMenuOpen && (
              <QuickMenuPopover
                agentPrompts={agentPrompts}
                roleLabel={activeRoleMeta.label}
                onPick={(prompt) => { setInputMessage(prompt); setQuickMenuOpen(false); }}
                onClose={() => setQuickMenuOpen(false)}
              />
            )}
          </div>
        </div>
      </div>
     </div>

      {/* 右侧 Context Panel：可折叠（当前项目 / 角色 / 指标 / 重点方向 / 数据来源） */}
      {contextCollapsed ? (
        <button
          className="context-toggle context-toggle-collapsed"
          onClick={() => setContextCollapsed(false)}
          title="展开工作台上下文"
        >
          ‹
        </button>
      ) : (
        <aside className="strategy-context">
          <div className="ctx-head">
            <h4 className="ctx-title">工作台上下文</h4>
            <button
              className="context-toggle"
              onClick={() => setContextCollapsed(true)}
              title="收起工作台上下文"
            >
              ›
            </button>
          </div>
          <div className="ctx-block">
            <span className="ctx-label">当前项目</span>
            <b className="ctx-value">{currentProject.name}</b>
            <span className="ctx-sub">{currentProject.category} · {currentProject.currentStage}</span>
          </div>
          {perfSnapshots.length > 0 && activePerfSnapshot && (
            <div className="ctx-block ctx-perf">
              <span className="ctx-label">演示投放数据</span>
              <div className="perf-demo-row">
                <span className="perf-demo-badge">DEMO</span>
                <b className="ctx-value">数据截至 {activePerfSnapshot.snapshot_time.slice(5, 16)}</b>
              </div>
              <label className="perf-select-wrap">
                <span>快照时间</span>
                <select
                  className="perf-select"
                  value={perfActiveId ?? ''}
                  disabled={perfSwitching}
                  onChange={(e) => handleSnapshotSwitch(Number(e.target.value))}
                >
                  {perfSnapshots.map(s => (
                    <option key={s.id} value={s.id}>{s.snapshot_name}</option>
                  ))}
                </select>
              </label>
              <span className="perf-hint">数据问答将基于所选快照回答</span>
            </div>
          )}
          <div className="ctx-block">
            <span className="ctx-label">当前角色</span>
            <b className="ctx-value">{activeRoleMeta.title}</b>
            <span className="ctx-sub">{activeRoleMeta.desc}</span>
          </div>
          <div className="ctx-block">
            <span className="ctx-label">当前业务目标</span>
            <p className="ctx-text">{currentProject.learningWorkflow.brief.businessObjective}</p>
          </div>
          <div className="ctx-block">
            <span className="ctx-label">近期核心指标</span>
            <div className="ctx-kpis">
              <div className="ctx-kpi"><span>累计消耗</span><b>{formatWan(currentProject.optimization.overview.totalSpend)}</b></div>
              <div className="ctx-kpi"><span>累计转化</span><b>{currentProject.optimization.overview.conversions.toLocaleString()}</b></div>
              <div className="ctx-kpi"><span>{currentProject.optimization.overview.conversionLabel}</span><b>¥{currentProject.optimization.overview.costPerConversion}</b></div>
              <div className="ctx-kpi"><span>平均CTR</span><b>{currentProject.optimization.overview.ctrPct}%</b></div>
            </div>
          </div>
          <div className="ctx-block">
            <span className="ctx-label">近期重点方向</span>
            <ul className="ctx-list">
              {currentProject.learningWorkflow.strategySummary.slice(0, 3).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="ctx-block ctx-source">
            <span className="ctx-label">数据 / Skill 来源</span>
            <p className="ctx-text">{currentProject.optimization.dataSourceNote}</p>
          </div>
        </aside>
      )}

      {/* 历史提问记录抽屉 */}
      {historyOpen && (
        <div className="history-drawer">
          <div className="history-header">
            <h3>历史提问记录</h3>
            <button className="history-close" onClick={() => setHistoryOpen(false)}>×</button>
          </div>
          {expanded ? (
            <div className="history-detail">
              <button className="history-back" onClick={() => setExpanded(null)}>← 返回列表</button>
              <p className="history-session-title">{expanded.session.title}</p>
              {expanded.messages.length === 0 && <p className="history-empty">该会话暂无消息</p>}
              {expanded.messages.map((m, i) => (
                <div key={i} className={`history-msg history-msg-${m.role}`}>
                  <div className="history-msg-role">{m.role === 'user' ? '我' : roleLabel(expanded.session.role)}</div>
                  <div className="history-msg-content">
                    {m.content.split('\n').map((line, j) => (
                      <p key={j}>{line || <br />}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="history-list">
              {sessions.length === 0 && <p className="history-empty">暂无提问记录</p>}
              {sessions.map(s => (
                <div key={s.id} className="history-item" onClick={() => viewSession(s.id)}>
                  <div className="history-item-title">{s.projectName} · {roleLabel(s.role)}</div>
                  <div className="history-item-meta">{s.messageCount} 条消息 · 创建于 {s.createdAt.slice(0, 16).replace('T', ' ')}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIDirector;
